import React from 'react';
import './deep-dives-styles.css';
import Header from '../components/header';
import GoToTop from '../components/go-to-top';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Articol from '../components/article';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import primarykey from '../assets/primarykey.png';
import lamdarole from '../assets/lamdarole.png';
import components from '../assets/golang-webapp-components.png';

class GoLangWebAppDynamo extends React.Component {
    sendEvent = () => {
        if(window.location.hostname === 'localhost' || window.location.hostname.indexOf('192.168.0') === 0) {
            return;
        }

        if(!sessionStorage.getItem('post-golang-webapp-3')) {
            sessionStorage.setItem('post-golang-webapp-3', true);

            fetch('https://09mclyfv7k.execute-api.eu-central-1.amazonaws.com/prod/log_event', {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({type: 'visit', name: 'Post GoLang Web app - Part 3', from: 'binaryfusion.ro'}),
            }).then((response) => response.json().then((responseData) => {
                sessionStorage.setItem('success', responseData.EventId);
            }));
        }
    }

    sendEvent = () => {
        if(window.location.hostname === 'localhost' || window.location.hostname.indexOf('192.168.0') === 0) {
            return;
        }

        if(!sessionStorage.getItem('post-dynamo')) {
            sessionStorage.setItem('post-dynamo', true);

            fetch('https://api.ipify.org?format=json')
                .then((ipResp) => ipResp.json().then((ipJson) => {
                    fetch('https://09mclyfv7k.execute-api.eu-central-1.amazonaws.com/prod/log_event', {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({type: 'post-dynamo', name: 'binaryfusion.ro', from: ipJson}),
                    }).then((response) => response.json().then((responseData) => {
                        sessionStorage.setItem('success', responseData.EventId);
                    }));
                })
            );
        }
    }

    componentDidMount() {
        if(window.location.hostname !== 'localhost' && window.location.hostname.indexOf('192.168.0') === -1) {
            console.log("Scrolled to top because hostname=" + window.location.hostname);
            window.scrollTo(0,0);
        }

        this.sendEvent();
    }

    render() {
        const codeStyle = {
            'textShadow': 'none',
            'border': 'none',
            'boxShadow': 'none',
        };

        return (
            <div>
                <Header />
                <GoToTop />

                <div className='page'>
                    <div className='title'>GoLang Webapp on AWS (Part 3) - Lambda and DynamoDB</div>
                    <div className='paragraph'>
                        Updated on 29 Aug 2024
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Plan</h5>
                        In this article I want to show the code I used to be able to use the CRUD operations on the <strong>AWS DynamoDB</strong> database and most importantly the errors I encountered and 
                        the method I used to overcome them. The general procedure I followed was to:<br/>
                        - Setup and local configuration<br/>
                        - Connecting to AWS<br/>
                        - Testing from Lambda function<br/>
                        - Testing from React front-end<br/>
                    </div>

                    <div className='side-story mt-2'>
                        <h2>Side story: CRUD</h2>

                        <div className='paragraph'>
                            CRUD stands for Create, Read, Update, Delete.
                        </div>

                        <div className='paragraph'>
                            DynamoDB is a <strong>non-relational</strong> type database. A non-relational database or NoSQL is simply a type of database that does not adhere to the
                            traditional relational model such as SQL. There are different ways for storing and retreiving data such as Document Stores, Column-Family Store, Graph or,
                            such in our case <strong>Key-Value</strong> Stores. Oposed to this, relational databases use SQL - Structured Query Language to store information in tables containing specific pieces and types
                            of data.
                        </div>
                    </div>

                    <h5>Setup and local configuration</h5>
                    <div className='paragraph'>
                        The go package I used for this task is the <inline-code>aws-sdk-go-v2</inline-code>. It has good documentation <a href='https://aws.github.io/aws-sdk-go-v2/docs/configuring-sdk/' target='_blank' rel='noreferrer'>here </a>
                        and I also used this <a target='_blank' rel='noreferrer' href='https://github.com/awslabs/amazon-dynamodb-local-samples/blob/main/DynamoDBLocal/src/main/java/aws/example/Main.java'>example </a>
                        and <a target='_blank' rel='noreferrer' href='https://github.com/aws/aws-sdk-go-v2/blob/096445ee1dbb/example/service/dynamodb/createTable/createTable.go'>create table example</a>.
                    </div>


                    <h5>Connecting to AWS</h5>

                    <div className='paragraph'>
                        <h5 className='title'>Getting the DB</h5>
                        This is the code I wrote that connects to the DynamoDB and initiate the principal database operations:

                        <SyntaxHighlighter language='go' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`type DBService struct {
	client *dynamodb.Client
	ctx    context.Context
}

func GetDbService() *DBService {
	ctx := context.TODO()
	cfg, err := config.LoadDefaultConfig(ctx, config.WithRegion("eu-central-1"))
	if err != nil {
		log.Fatal(err)
	}

	provider := cfg.Credentials
	cred, _ := provider.Retrieve(ctx)

	dbService := DBService{}
	dbService.client = dynamodb.NewFromConfig(cfg)
	dbService.ctx = ctx

	return &dbService
} `}
                        </SyntaxHighlighter>

                        <div className='side-story mt-2 mb-2'>
                            <h2>Side story: Database context</h2>

                            <div className='paragraph'>
                                <inline-code>context</inline-code> objects are used for configuring the lifetime of a request, to obtain a method of canceling requests,
                                customize the request or the response within the AWS service client with <inline-code>WithValue</inline-code> method, request tracing for debugging and monitoring purposes or setting requests timeouts.
                            </div>

                            <div className='paragraph'>
                                The <inline-code>context.TODO()</inline-code> method provides a basic, empty configuration generally useful when there isn't enough data available to decide which context is appropriate. In my case, for testing purposes it felt right.
                            </div>

                        </div>

                        Same as with other AWS services, we need the user credentials to access the DB and the geographic area. The SDK will try to load the configuration from various external sources, in the following order:<br/>
                        - 1. Environment variables<br/>
                        - 2. Shared credentials file<br/>
                        - 3. AWS configuration file<br/>
                        - 4. In-memory configuration<br/>
                        In my case here I get them from the the local AWS configuration file at <inline-code>~/.aws/config</inline-code>. This file may contain multiple  profiles, that you can use for different purposses as they can have different levels of access. Either we can set the desired profile with <inline-code>config.WithProfile("profile-name")</inline-code> when loading the config:
                        
                        <SyntaxHighlighter language='go' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`cfg, err := config.LoadDefaultConfig(ctx, config.WithProfile("profile-name"))
if err != nil {
    panic(err)
}`}
                        </SyntaxHighlighter>
                        or just let it get the default config profile.<br/>
                    </div>

                    <div className='paragraph'>
                        Requests for operations on the database will be made using the instance of DBService. 
                        Inside, it will use the <inline-code>client *dynamodb.Client</inline-code> to make the requests to AWS SDK like this:
                        <SyntaxHighlighter language='go' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`func (db *DBService) PutRobot(item map[string]types.AttributeValue) error {

	input := &dynamodb.PutItemInput{
		TableName: aws.String("Robots"),
		Item:      item,
	}

	_, err := db.client.PutItem(db.ctx, input)
	return err
} `}
                        </SyntaxHighlighter>
                        I will come back to this in more detail in a next article about the details of the architecture.
                    </div>


                    <div className='paragraph'>
                        <h5 className='title'>Creating a table</h5>
                        I created the table manually this time, or more precisly I wrote a test with exactly this purpose. I will update this part in the future to automatically create the table if it doesn't exists. For your use I add here the code to create a table:

                        <SyntaxHighlighter language='go' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`func (db *DBService) createRobotsTable() {
	param := &dynamodb.CreateTableInput{
		AttributeDefinitions: []types.AttributeDefinition{
			{
				AttributeName: aws.String("RobotId"),
				AttributeType: types.ScalarAttributeTypeS,
			},
			{
				AttributeName: aws.String("Name"),
				AttributeType: types.ScalarAttributeTypeS,
			},
		},
		KeySchema: []types.KeySchemaElement{
			{
				AttributeName: aws.String("RobotId"),
				KeyType:       types.KeyTypeHash,
			},
			{
				AttributeName: aws.String("Name"),
				KeyType:       types.KeyTypeRange,
			},
		},
		ProvisionedThroughput: &types.ProvisionedThroughput{
			ReadCapacityUnits:  aws.Int64(5),
			WriteCapacityUnits: aws.Int64(5),
		},
		TableName: aws.String("Robots"),
	}

	_, err := db.client.CreateTable(context.TODO(), param)
	if err != nil {
		log.Fatal("Cannot create table. " + err.Error())
	}
}`}
                        </SyntaxHighlighter>
                    </div>

                    <div className='issue'>
                        <div className='issue_title'><FontAwesomeIcon className='issue-red' icon={faTriangleExclamation}/> Number of KeySchema attributes</div>
                        By mistake I first tried to add as <inline-code>types.KeySchemaElement</inline-code>  all the fields in my object I plan to save in this table. This, of course is very wrong since, as I mentioned in the begining of this post, DynamoDB stores data in Key-Value pairs. This means there is no concept here as a Column. I don't need to decide at table creation time the number of pair I will store and what those pairs represent or what type they are. I received this error:<br/><br/>

                        <span className='error-red'>One or more parameter values were invalid: Number of attributes in KeySchema does not exactly match number of attributes defined in AttributeDefinitions</span><br/><br/>
                        
                        The solution to this problem is to leave only 2 <inline-code>KeySchemaElement</inline-code> when creating the <inline-code>AttributeDefinitions</inline-code>: the unique Id field and another sorting field, in my case the Name.
                    </div>
                    <div className='space10' />

                    <div className='paragraph'>
                        <h5 className='title'>Key Schema</h5>
                        The <strong>Key Schema</strong> of a table defines the organization and structure of data within the database. It also holds the constraints: primary keys, foreign keys, unique contraints. In this case the field RobotId is the primary key. You can check that the desired result is obtained from the AWS interface:
                    </div>


                    <img alt='architecture components for web app' className='img-row' src={primarykey} />

                    <div className='paragraph'>
                       PartitionKey is the primary key. The partition key in a DynamoDB table is the unique identifier for each item. Each item in a DynamoDB table must have a unique partition key value. DynamoDB uses the partition key value to determine which server in the cluster will store the item.
                    </div>


                    <div className='issue'>
                        <div className='issue_title'><FontAwesomeIcon className='issue-red' icon={faTriangleExclamation}/> Get credentials error</div>
                        After succesfully creating the table, when trying to get db items in my Lambda code, I received the following error:<br/><br/>

                        <span className='error-red'>Cannot list tables. operation error DynamoDB: ListTables, get identity: get credentials: failed to refresh cached credentials, no EC2 IMDS role found, operation error ec2imds: GetMetadata, request canceled, context deadline exceeded</span><br/><br/>
                        The problem here actually is that the code did not had the right to access the database. The solution is to update <inline-code>.aws/credentials</inline-code>, on the default profile, to contain the region, the access key id and the secret access key.
                    </div>
                    <div className='space10' />

                    <div className='paragraph'>
                        <h5 className='title'>IDMS Role</h5>
                        Researching the error above I learned a few pieces of vital information. <strong>IDMS</strong> stands for Instance Metadata Service and is a service that provides information about the EC2 instance itself, including the IAM role attached to it. 
                        The <strong>EC2 instance</strong> is a virtual server that you can set up on demand in the cloud. More on this <a target='_blank' rel='noreferrer' href='https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/ec2-instance-metadata.html'>here</a>.
                        The Instance Metadata Service runs locally on every EC2 instance. The application with IMDS enabled, through AWS SDK sends requests to IMDS end-points and further on to AWS where the received requests must have
                        the correct region configured and signed with the correct credentials to succeed.
                    </div>

                    <div className='paragraph'>
                        The conclution is that the error above was showing that the credentials were missing. Two configurations must be in place: <br/>
                        The AWS config file <inline-code>.aws/config</inline-code> must contain:<br/>
                        <SyntaxHighlighter language='bash' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`[default]
region=eu-central-1`}
                        </SyntaxHighlighter>


                        And AWS credentials file <inline-code>.aws/credentials</inline-code> must contain
                        <SyntaxHighlighter language='bash' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`[default]
aws_access_key_id=<YOUR_ACCESS_KEY_ID>
aws_secret_access_key=<YOUR_SECRET_ACCESS_KEY>`}
                        </SyntaxHighlighter>
                        Access key and secred access key you obtain by creating a user with the right permissions. 
                        You can do that in IAM service in AWS. You also can create access key for an existing user by selecting the user, in IAM 
                        service, going to Security Credentials tab and to Access keys section. There, "Create access key" button should be visible.
                    </div>

                    <div className='paragraph'>
                        <h5 className='title'>Permissions</h5>
                        The first step to add the correct permissions is to know which <strong>Role</strong> the Lambda function code is assuming 
                        when contacting the DynamoDB and actually when is executing. You can find that by using the AWS platform UI, at Lambda service, 
                        select the correct Lambda function, select "Configuration" tab and you can see under "Execution role", "Role name" the name of 
                        the role we have to configure with the correct permission.<br/>
                        To get this information faster, you can also use the <strong>aws cli</strong> with a command like this:
                        <SyntaxHighlighter language='bash' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`aws lambda list-functions | jq '.Functions[] | {N: .FunctionName, R: .Role}'`}
                        </SyntaxHighlighter>
                        Here, the output from <inline-code>list-functions</inline-code> is piped to the utility <inline-code>jq</inline-code>. This is a json parser that 
                        I am using in this case to generate a smaller object that contains only the function name and the role.
                    </div>

                    <div className='paragraph'>
                        Now, that we have the role, we can set the minimum necessary permissions on that role to be able to exectute and communicate with DB service and any 
                        other services for that matter. In <strong>IAM Console</strong>,
                        select "Role" from the left vertical menu and select "Permissions" tab. Here you should find <strong>DynamoDBWriteAccess</strong> permission. The important 
                        information here is that it should have write acess for the correct database - identified by the ARN id.
                    </div>

                    <img alt='db acess lambda role' className='img-row high' src={lamdarole} />

                    <div className='paragraph'>
                        To get the ARN for the desired table you can go to <strong>DynamoDB</strong> console, "Tables" in the left vertical menu, select the correct 
                        table name, unde "Overview" tab, under "Additional Info", there is Amazon Resource Name (ARN). The corresponding aws cli command:
                        <SyntaxHighlighter language='bash' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`aws dynamodb list-tables
aws dynamodb describe-table --table-name Robots | jq '.Table | {N: .TableName, arn: .TableArn}'`}
                        </SyntaxHighlighter>
                        You need to know the name of the table to be able to run the <inline-code>describe-table</inline-code> command so for this reason I added the first command.
                        Note that you can add the <strong>ARN</strong> for multiple tables to a single permission.
                    </div>

                    <div className='paragraph'>
                        After Lambda permissions are in place and local configuration files are correctly setup, you should be able to make operations
                        to DB by making REST calls to the API Gateway.
                    </div>

                    <img alt='architecture components for web app' className='img-row' src={components} />
                    
                    <div className='paragraph'>
                        The code that makes those calls in my case looks like this:

                        <SyntaxHighlighter language='go' style={dark} showLineNumbers='true' customStyle={codeStyle}>
                            {`package database

import (
	"context"
	"fmt"
	"log"
	"os"

	"github.com/aws/aws-sdk-go-v2/aws"
	"github.com/aws/aws-sdk-go-v2/config"
	"github.com/aws/aws-sdk-go-v2/service/dynamodb"
	"github.com/aws/aws-sdk-go-v2/service/dynamodb/types"
)


type DBService struct {
	client *dynamodb.Client
	ctx    context.Context
	Robots []map[string]string
}

func (db *DBService) PutRobot(item map[string]types.AttributeValue) error {

	input := &dynamodb.PutItemInput{
		TableName: aws.String("Robots"),
		Item:      item,
	}

	_, err := db.client.PutItem(db.ctx, input)
	return err
}

func (db *DBService) GetRobots(id *string) ([]map[string]types.AttributeValue, error) {
	input := &dynamodb.ScanInput{
		TableName: aws.String("Robots"),
	}

	output, err := db.client.Scan(db.ctx, input)
	if err != nil {
		fmt.Fprintf(os.Stdout, "Error: %s", err)
		return []map[string]types.AttributeValue{}, err
	}

	return output.Items, err
}`}
                        </SyntaxHighlighter>

                    </div>

                    <h5>Next</h5>
                    <div className='paragraph'>
                        I will conclude here this post about how I managed the connection between the Lambda function and my DynamoDB AWS service. 
                        Please send an email if you have any suggestion of how I can improve the quality of the informatin posted here.
                        In the next article I will describe the general principles of the architecture I used and the implementation details.
                    </div>

                    <div className='articles mt-5'>
                        <Articol title='GoLang Web app on AWS (Part 2) - Front-end and CORS' to={'/dp-golang-webapp-cors'} />
                        <Articol title='GoLang Web app on AWS (Part 4) - Architecture' to={'/dp-golang-webapp-architecture'} />
                    </div>

                    <div className='mb-10'></div>
                </div>
            </div>
        )
    }
}

export default GoLangWebAppDynamo;

